import { useCallback, useEffect, useState } from 'react'
import { AccessNotice } from './AccessNotice'
import { AccessState, fetchMainLoginUrl, probeAccess } from './access'
import { useClientConfig } from './client-config'
import { ChatPage } from './chat/ChatPage'
import { Sidebar } from './chat/Sidebar'
import { Icon, PATH } from './icons'
import { consumeLaunchPrefill } from './launch-prefill'
import { parseRoute, useHashRoute } from './route'

function App() {
  const route = useHashRoute()
  // 進站先探一次守門。沒有這一步，未登入的人看到的是「建立對話失敗（401）」——
  // 那是給工程師看的字，使用者不知道那代表要回去登入。
  const [access, setAccess] = useState<AccessState | null>(null)
  const [loginUrl, setLoginUrl] = useState<string | null>(null)
  // 連結帶入的預填訊息：確定能用才讀（被擋下時網址原樣保留，重新整理還在）。
  // 與 access 同一批設定，ChatPage 第一次掛載就拿得到；StrictMode 重跑時第二次讀不到，不覆蓋
  const [launchPrefill, setLaunchPrefill] = useState<string | null>(null)
  useEffect(() => {
    const settle = (state: AccessState) => {
      if (state === 'ok') {
        const prefill = consumeLaunchPrefill(parseRoute(window.location.hash).conversationId === null)
        setLaunchPrefill((prev) => prev ?? prefill)
      }
      setAccess(state)
    }
    void probeAccess().then(settle).catch(() => settle('ok'))
  }, [])
  // 只有未登入才需要登入網址
  useEffect(() => {
    if (access === 'unauthenticated') void fetchMainLoginUrl().then(setLoginUrl)
  }, [access])
  const [drawerOpen, setDrawerOpen] = useState(false)
  // 「新對話」按了幾次。它是 ChatPage 的 key 的一部分，所以停在 #/ 再按一次
  // 也真的重開一條，而不是什麼都沒發生。
  const [newChatNonce, setNewChatNonce] = useState(0)
  // 側欄清單的重拉訊號：開了新對話、或一輪講完（標題此時才有值）就 +1
  const [historyVersion, setHistoryVersion] = useState(0)
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  // 開發元素（工具 JSON、回饋按鈕、模型切換）只在伺服器說是 dev 時才渲染（#1724、#1726）
  const { showDevTools, models, defaultModel, maxSteps, thinkingBudget } = useClientConfig()

  // 手機的覆蓋式抽屜要有出口：遮罩之外，Esc 也要能關
  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawerOpen])

  const closeDrawer = useCallback(() => setDrawerOpen(false), [])

  const startNewChat = useCallback(() => {
    setLaunchPrefill(null)
    setNewChatNonce((n) => n + 1)
    setDrawerOpen(false)
    if (window.location.hash !== '#/') window.location.hash = '#/'
  }, [])

  const bumpHistory = useCallback(() => setHistoryVersion((v) => v + 1), [])
  // 切去續談或按新對話後預填作廢，之後再開的新對話不會帶到
  useEffect(() => {
    if (route.conversationId !== null) setLaunchPrefill(null)
  }, [route.conversationId])

  if (access === null) return <div className="access-probe" />
  if (access !== 'ok') return <AccessNotice state={access} loginUrl={loginUrl} />

  return (
    <div className={`shell ${drawerOpen ? 'drawer-open' : ''}`}>
      <Sidebar
        activeConversationId={activeConversationId}
        version={historyVersion}
        onNewChat={startNewChat}
        onNavigate={closeDrawer}
      />
      {/* 遮罩只在手機抽屜開啟時吃得到事件，桌機的側欄是常駐的 */}
      <div className="side-scrim" onClick={closeDrawer} />

      <div className="canvas">
        <div className="topbar">
          <button className="hamburger" aria-label="開啟側欄" onClick={() => setDrawerOpen(true)}>
            <Icon d={PATH.menu} />
          </button>
        </div>
        {/* key 換掉就整個重掛：續談與新對話各自從乾淨的狀態開始，
            也讓上一條的串流在卸載時被停掉（見 ChatPage 的卸載處理）。 */}
        <ChatPage
          key={route.conversationId ?? `new-${newChatNonce}`}
          conversationId={route.conversationId}
          onConversationReady={setActiveConversationId}
          onTurnFinished={bumpHistory}
          showDevTools={showDevTools}
          modelOptions={models}
          defaultModel={defaultModel ?? null}
          stepBounds={maxSteps ?? null}
          thinkingBounds={thinkingBudget ?? null}
          prefill={route.conversationId === null ? launchPrefill : null}
        />
      </div>
    </div>
  )
}

export default App
